import type { DrawingEntity, QualityIssue } from '../contract/types.js';

export interface MergeResult {
  readonly entities: DrawingEntity[];
  readonly issues: QualityIssue[];
  readonly counts: {
    vector: number;
    raster: number;
    /** Positional matches whose types disagree. Both are kept. */
    conflicts: number;
    /** Raster duplicates of a vector entity, dropped. */
    suppressed: number;
  };
}

/** Below this a raster entity is emitted but the job may not read as clean. */
const LOW_CONFIDENCE = 0.5;

/**
 * How far apart two bounding-box centres may be, in multiples of the tolerance,
 * and still describe the same place.
 *
 * Cross-type matching compares centres rather than corners, because a raster LINE
 * standing in for a vector CIRCLE has a legitimately different extent — the
 * diameter of a circle shares its centre and nothing else. Centres alone would
 * match a 2 mm tick against a concentric drawing frame, so the extents have to be
 * comparable too.
 */
const CROSS_TYPE_EXTENT_RATIO = 4;

/**
 * Vector wins, always.
 *
 * Three rules, and the first is the one that matters: **a raster candidate never
 * overwrites a matching vector entity.** Vector geometry is exact and raster
 * geometry is estimated, so preferring the estimate would make this product worse
 * the better its input was — which is the opposite of what a hybrid pipeline is
 * for. Confidence does not change that; a perfectly confident estimate is still
 * an estimate.
 *
 * Where a raster candidate matches a vector entity in *position* but disagrees in
 * *kind*, both survive and the raster one is flagged `merge_candidate`. Dropping
 * one silently would be making the reviewer's decision for them, and it is
 * exactly the case where the machine is least entitled to.
 *
 * A raster entity below `lowConfidence` is emitted with a `low_confidence` issue.
 * It is not hidden — hiding it would understate what the scan contains — but the
 * issue means the job cannot be reported as clean.
 *
 * **Same kind and different kind are compared differently, and they have to be.**
 * A duplicate is corner-for-corner: the same line found twice occupies the same
 * box. A disagreement is not, because the raster shape standing in for a vector
 * one has its own extent — the case this exists for is a LINE reported where the
 * vector layer has the CIRCLE it is a diameter of, and those two boxes share only
 * a centre. So a conflict is a centre within tolerance *and* extents within
 * `CROSS_TYPE_EXTENT_RATIO`, which flags the disagreement without flagging every
 * small thing that happens to sit in the middle of a big one.
 */
export function mergeRaster(
  vector: readonly DrawingEntity[],
  raster: readonly DrawingEntity[],
  { tolerance, lowConfidence = LOW_CONFIDENCE }: { tolerance?: number; lowConfidence?: number } = {},
): MergeResult {
  const limit = tolerance ?? defaultTolerance(vector.length > 0 ? vector : raster);
  const issues: QualityIssue[] = [];
  const kept: DrawingEntity[] = [...vector];
  let conflicts = 0;
  let suppressed = 0;

  // Sorted before matching, so the result is a property of the geometry rather
  // than of the order the detector happened to report in.
  for (const candidate of [...raster].sort(compareEntities)) {
    const duplicate = vector.find(
      (entity) => entity.type === candidate.type && sameBox(entity, candidate, limit),
    );
    if (duplicate) {
      suppressed += 1;
      continue;
    }

    const disagreement = vector.find(
      (entity) => entity.type !== candidate.type && samePlace(entity, candidate, limit),
    );
    if (disagreement) {
      conflicts += 1;
      issues.push({
        kind: 'merge_candidate',
        count: 1,
        pageIndex: candidate.source.pageIndex,
        bbox: candidate.source.bbox,
        detail:
          `a raster ${candidate.type} sits within tolerance of a vector ${disagreement.type}; ` +
          'both are kept so the reviewer decides',
        entityIds: [candidate.id, disagreement.id],
      });
    }

    if ((candidate.confidence ?? 0) < lowConfidence) {
      issues.push({
        kind: 'low_confidence',
        count: 1,
        pageIndex: candidate.source.pageIndex,
        bbox: candidate.source.bbox,
        // Three decimals. The raw double reads as `0.17896206655806376` on screen,
        // and a confidence printed to seventeen places claims a precision the
        // detector does not have -- on the one surface whose job is not to
        // overstate what a scan is worth.
        detail: `raster ${candidate.type} at confidence ${(candidate.confidence ?? 0).toFixed(3)}`,
        entityIds: [candidate.id],
      });
    }

    kept.push(candidate);
  }

  return {
    entities: kept.sort(compareEntities),
    issues,
    counts: {
      vector: vector.length,
      raster: kept.length - vector.length,
      conflicts,
      suppressed,
    },
  };
}

/** The acceptance matcher's tolerance: 0.2 mm, or a thousandth of the diagonal. */
function defaultTolerance(entities: readonly DrawingEntity[]): number {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const entity of entities) {
    const [x0, y0, x1, y1] = entity.source.bbox;
    minX = Math.min(minX, x0);
    minY = Math.min(minY, y0);
    maxX = Math.max(maxX, x1);
    maxY = Math.max(maxY, y1);
  }
  if (!Number.isFinite(minX)) return 0.2;
  return Math.max(0.2, Math.hypot(maxX - minX, maxY - minY) * 0.001);
}

/** Corner for corner: the same shape found twice occupies the same box. */
function sameBox(a: DrawingEntity, b: DrawingEntity, limit: number): boolean {
  const [ax0, ay0, ax1, ay1] = a.source.bbox;
  const [bx0, by0, bx1, by1] = b.source.bbox;
  return (
    Math.abs(ax0 - bx0) <= limit &&
    Math.abs(ay0 - by0) <= limit &&
    Math.abs(ax1 - bx1) <= limit &&
    Math.abs(ay1 - by1) <= limit
  );
}

/** The same place at a comparable size, for shapes whose kinds disagree. */
function samePlace(a: DrawingEntity, b: DrawingEntity, limit: number): boolean {
  const [ax0, ay0, ax1, ay1] = a.source.bbox;
  const [bx0, by0, bx1, by1] = b.source.bbox;
  const centreApart = Math.max(
    Math.abs((ax0 + ax1) / 2 - (bx0 + bx1) / 2),
    Math.abs((ay0 + ay1) / 2 - (by0 + by1) / 2),
  );
  if (centreApart > limit) return false;

  const aSpan = Math.hypot(ax1 - ax0, ay1 - ay0);
  const bSpan = Math.hypot(bx1 - bx0, by1 - by0);
  const larger = Math.max(aSpan, bSpan);
  const smaller = Math.min(aSpan, bSpan);
  // A zero-span entity next to anything is not a comparable extent.
  if (larger === 0) return true;
  if (smaller === 0) return false;
  return larger / smaller <= CROSS_TYPE_EXTENT_RATIO;
}

function compareEntities(a: DrawingEntity, b: DrawingEntity): number {
  const [ax, ay] = a.source.bbox;
  const [bx, by] = b.source.bbox;
  return ay - by || ax - bx || a.type.localeCompare(b.type) || a.id.localeCompare(b.id);
}
